import React from 'react';
import { useTranslation } from 'react-i18next';
import { Star, X, Check } from 'lucide-react';
import type { TestimonialResponse } from '@clareefai/types';

interface SelectReviewsModalProps {
  open: boolean;
  onClose: () => void;
  /** Eligible (published / featured) reviews to choose from. */
  reviews: TestimonialResponse[];
  selectedIds: string[];
  onChange: (ids: string[]) => void;
}

/**
 * Hand-pick the published reviews to feature in the reviews widget. Follows the
 * filtering/grid pattern of the public ReviewsDialog but in selection mode.
 */
const SelectReviewsModal: React.FC<SelectReviewsModalProps> = ({
  open,
  onClose,
  reviews,
  selectedIds,
  onChange,
}) => {
  const { t } = useTranslation();
  if (!open) return null;

  const toggle = (id: string) => {
    onChange(selectedIds.includes(id) ? selectedIds.filter((x) => x !== id) : [...selectedIds, id]);
  };

  return (
    <div className="fixed inset-0 z-[1000] flex items-center justify-center p-4 bg-black/50" onClick={onClose}>
      <div
        className="bg-white rounded-2xl shadow-2xl w-full max-w-3xl max-h-[85vh] flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between p-5 border-b border-gray-200">
          <div>
            <h2 className="text-lg font-bold text-gray-900">{t('reviewsWidget.modal.title', 'Select Reviews')}</h2>
            <p className="text-sm text-gray-600">
              {t('reviewsWidget.modal.subtitle', 'Choose the published reviews to feature in your widget.')}
            </p>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600" aria-label="Close">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="p-5 overflow-y-auto flex-1">
          {reviews.length === 0 ? (
            <p className="text-sm text-gray-500 text-center py-10">
              {t('reviewsWidget.modal.empty', 'No published reviews yet.')}
            </p>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {reviews.map((r) => {
                const selected = selectedIds.includes(r.id);
                return (
                  <button
                    key={r.id}
                    type="button"
                    onClick={() => toggle(r.id)}
                    className={`text-left border rounded-xl p-4 transition-all ${
                      selected ? 'border-purple-500 ring-2 ring-purple-200 bg-purple-50/40' : 'border-gray-200 hover:border-gray-300'
                    }`}
                  >
                    <div className="flex items-center justify-between mb-2">
                      <span className="font-semibold text-gray-900 text-sm truncate">{r.promoterName}</span>
                      <span
                        className={`w-5 h-5 rounded-full flex items-center justify-center flex-shrink-0 ${
                          selected ? 'bg-purple-600 text-white' : 'border border-gray-300'
                        }`}
                      >
                        {selected && <Check className="w-3 h-3" />}
                      </span>
                    </div>
                    {r.promoterCompany && <p className="text-xs text-gray-500 mb-1">{r.promoterCompany}</p>}
                    {typeof r.rating === 'number' && (
                      <div className="flex mb-2">
                        {[...Array(5)].map((_, i) => (
                          <Star
                            key={i}
                            className={`w-3.5 h-3.5 ${i < (r.rating ?? 0) ? 'text-yellow-400 fill-current' : 'text-gray-300'}`}
                          />
                        ))}
                      </div>
                    )}
                    {r.content && <p className="text-xs text-gray-600 line-clamp-3">{r.content}</p>}
                  </button>
                );
              })}
            </div>
          )}
        </div>

        <div className="flex items-center justify-between p-5 border-t border-gray-200">
          <span className="text-sm text-gray-600">
            {t('reviewsWidget.modal.selected', '{{count}} selected', { count: selectedIds.length })}
          </span>
          <button
            onClick={onClose}
            className="px-4 py-2 rounded-lg text-sm font-medium text-white bg-purple-600 hover:bg-purple-700"
          >
            {t('reviewsWidget.modal.done', 'Done')}
          </button>
        </div>
      </div>
    </div>
  );
};

export default SelectReviewsModal;
